import { makeStyles, Text, Button } from "@fluentui/react-components";
import { CheckmarkCircle16Filled, PeopleTeam48Regular, ShieldCheckmark48Regular, ArrowTrendingLinesRegular } from "@fluentui/react-icons";
import { Link } from "react-router-dom";
import { dolphinTokens } from "../../utils/Theme";
import { useTranslation } from "react-i18next";
import TitleText from "../../components/titleText/TitleText";

const useStyles = makeStyles({
    section: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "auto 1fr",
        alignItems: "start",
        justifyContent: "center",
        gap: "3rem",
        padding: "4rem 2rem",
        maxWidth: "1400px",
        margin: "0 auto",
        "@media (max-width: 900px)": {
            gridTemplateColumns: "1fr",
            gridTemplateRows: "auto auto auto",
            gap: "2.5rem",
        },
    },
    titleWrapper: {
        gridColumn: "1 / -1",
        width: "100%",
        display: "flex",
        justifyContent: "center",
    },
    leftCol: {
        flex: "1 1 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        gap: "2rem",
        "@media (max-width: 900px)": {
            alignItems: "center",
            width: "100%",
        },
    },
    textBlock: {
        display: "flex",
        flexDirection: "column",
        gap: "0.4rem",
    },
    sectionTitle: {
        color: dolphinTokens.colorDarkBlue,
        lineHeight: "1.2",
        marginBottom: "0.25rem",
        "@media (max-width: 900px)": {
            textAlign: "center",
        },
    },
    sectionText: {
        color: "#869ab8",
        lineHeight: "1.4",
        marginTop: "0 !important",
        marginBottom: "0 !important",
        "@media (max-width: 900px)": {
            textAlign: "center",
        },
    },
    primaryBtn: {
        backgroundColor: "#00394c !important",
        color: "white !important",
        border: "none !important",
        padding: "1rem 1.5rem !important",
        fontWeight: "600 !important",
        borderRadius: "0.5rem !important",
        fontSize: "1rem !important",
        cursor: "pointer !important",
        transition: "transform 0.2s ease !important",
        width: "auto !important",
        ":hover": {
            transform: "translateY(-3px) !important",
        },
    },
    secondaryBtn: {
        backgroundColor: "white !important",
        color: "#00394c !important",
        border: "1px solid #00394c !important",
        padding: "1rem 1.5rem !important",
        fontWeight: "600 !important",
        borderRadius: "0.5rem !important",
        fontSize: "1rem !important",
        cursor: "pointer !important",
        transition: "transform 0.2s ease !important",
        width: "auto !important",
        ":hover": {
            transform: "translateY(-3px) !important",
        },
    },
    imageRow: {
        display: "flex",
        flexDirection: "row",
        gap: "2rem",
        justifyContent: "flex-start",
        flexWrap: "wrap",
        "@media (max-width: 900px)": {
            justifyContent: "center",
        },
    },
    imageItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "0.5rem",
        flex: "1 1 90px",
        maxWidth: "130px",
    },
    featureIconCircle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "72px",
        height: "72px",
        borderRadius: "50%",
        backgroundColor: "#eef6f4",
        color: dolphinTokens.colorLightBlue,
        fontSize: "2rem",
    },
    imageTitle: {
        fontWeight: "700",
        fontSize: "0.9rem",
        textAlign: "center",
        color: "#1a1a2e",
        lineHeight: "1.3",
    },
    imageSubtitle: {
        fontWeight: "400",
        fontSize: "0.8rem",
        textAlign: "center",
        color: "#869ab8",
        lineHeight: "1.4",
    },
    rightCol: {
        flex: "1 1 0",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "1rem",
    },
    tile: {
        position: "relative",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        borderRadius: "10px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        overflow: "hidden",
        minHeight: "110px",
        transition: "box-shadow 0.2s, transform 0.2s",
        ":hover": {
            boxShadow: "0 6px 20px rgba(0,0,0,0.13)",
            transform: "translateY(-2px)",
        },
    },
    tileImage: {
        position: "absolute",
        inset: "0",
        width: "100%",
        height: "100%",
        objectFit: "cover",
        zIndex: 0,
    },
    tileOverlay: {
        position: "absolute",
        inset: "0",
        background: "linear-gradient(to right, rgba(0,57,76,0.77) 10%, rgba(0,57,76,0.05) 60%)",
        zIndex: 1,
    },
    tileContent: {
        position: "relative",
        zIndex: 2,
        flex: 1,
        padding: "1.25rem 1.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
    },
    tileTitle: {
        fontWeight: "700",
        fontSize: "0.85rem",
        color: "white",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        marginBottom: "0.2rem",
    },
    bulletList: {
        display: "flex",
        flexDirection: "column",
        gap: "0.3rem",
    },
    bulletItem: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        color: "rgba(255,255,255,0.9)",
        fontSize: "0.9rem",
    },
    bulletIcon: {
        color: "rgba(255,255,255,0.8)",
        flexShrink: 0,
    },
});

const featureIcons = [
    <PeopleTeam48Regular />,
    <ShieldCheckmark48Regular />,
    <ArrowTrendingLinesRegular />,
];

export default function IntroSection() {
    const styles = useStyles();
    const { t } = useTranslation("home");
    const title = t("introSection.mainTitle");
    const description = t("introSection.description");

    const featureImages = t("introSection.features", { returnObjects: true }) as { src: string; title: string; subtitle: string }[];
    const serviceTiles = t("introSection.tiles", { returnObjects: true }) as { title: string; bullets: string[]; imageSrc: string; imageAlt: string }[];

    return (
        
        <div className={styles.section}>
            <div className={styles.titleWrapper}>
                <TitleText
                    title={title}
                    titleHighlightedWords={2}
                    subtitle={description}
                    alignment="center"
                    width="100%"
                    padding="0"
                    marginBottom="1.5rem"
                    titleMargin="0.5rem 0"
                />
            </div>
            <div className={styles.leftCol} style={{ gridColumn: "1 / span 1", gridRow: "2 / span 1" }}>
                <div className={styles.textBlock}>
                    <Text block as="h2" size={800} className={styles.sectionTitle}>
                        {t("introSection.title")}
                    </Text>
                    <Text block as="p" size={500} className={styles.sectionText} style={{ paddingBottom: "0.5rem" }}>
                        {t("introSection.text1")}
                    </Text>
                    <Text block as="p" size={500} className={styles.sectionText}>
                        {t("introSection.text2")}
                    </Text>
                </div>
                <div style={{ display: "flex", flexDirection: "row", gap: "1rem", alignItems: "center", flexWrap: "nowrap" }}>
                    <Link to="/contact">
                        <Button appearance="primary" className={styles.primaryBtn}>
                            {t("introSection.primaryCta")}
                        </Button>
                    </Link>
                    <Link to="/services">
                        <Button appearance="outline" className={styles.secondaryBtn}>
                            {t("introSection.secondaryCta")}
                        </Button>
                    </Link>
                </div>
                <div className={styles.imageRow}>
                    {featureImages.map((item, index) => (
                        <div key={item.title} className={styles.imageItem}>
                            <div className={styles.featureIconCircle}>
                                {featureIcons[index]}
                            </div>
                            <span className={styles.imageTitle}>{item.title}</span>
                            <span className={styles.imageSubtitle}>{item.subtitle}</span>
                        </div>
                    ))}
                </div>
            </div>
            <div className={styles.rightCol}>
                {serviceTiles.map((tile) => (
                    <div key={tile.title} className={styles.tile}>
                        <img
                            src={tile.imageSrc}
                            alt={tile.imageAlt}
                            className={styles.tileImage}
                            loading="lazy"
                        />
                        <div className={styles.tileOverlay} />
                        <div className={styles.tileContent}>
                            <span className={styles.tileTitle}>{tile.title}</span>
                            <div className={styles.bulletList}>
                                {tile.bullets.map((bullet) => (
                                    <div key={bullet} className={styles.bulletItem}>
                                        <CheckmarkCircle16Filled className={styles.bulletIcon} />
                                        <Text size={300}>{bullet}</Text>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}